import { endOfToday, startOfToday } from "date-fns";
import { BugNoteEntity } from "./BugNoteEntity";
import { BugGeneratedEntity } from "./generated/BugGeneratedEntity";
import { PhaseEntity } from "./PhaseEntity";
import { BugAnnotationEntity } from "./BugAnnotationEntity";
import { comparatorForType } from "@archipad-js/core/query";
import { ProjectUserEntity } from "./ProjectUserEntity";
import { SignOffEntity } from "./SignOffEntity";

export abstract class BugEntity extends BugGeneratedEntity {
    /**
     * @deprecated Issue phase is now mandatory, use phase instead.
     */
    public get currentPhase(): PhaseEntity {
        return this.phase;
    }

    public get stateNoteCache(): BugNoteEntity | null {
        return (
            this.notes
                .slice()
                .sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime())
                .find((note) => !!note.bugState) ?? null
        );
    }

    public get lastModified(): Date {
        return this.stateNoteCache?.date ?? this.stateNoteCache?.visit?.date ?? this.visit?.date ?? this.creationDate;
    }

    public get creator(): ProjectUserEntity | null {
        const sortedNotes = this.notes.slice().sort((a, b) => a.creationDate.getTime() - b.creationDate.getTime());
        const firstNote = sortedNotes[0];
        return firstNote?.user ?? null;
    }

    public get isFinal(): boolean {
        return this.signOff !== null;
    }

    /**
     * The sign-off of the bug: a delivery bug holds it (SignOffService.approveSignOff()), its construction copies
     * (relatedChildBugs) only link it from their history notes.
     */
    public get resolvedSignOff(): SignOffEntity | null {
        if (this.signOff) {
            return this.signOff;
        }
        if (this.relatedParentBug?.signOff) {
            return this.relatedParentBug.signOff;
        }
        const signOffNote = this.notes
            .filter((note) => !!note.signOff)
            .sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime())[0];
        return signOffNote?.signOff ?? null;
    }

    public get isClosed(): boolean {
        return Boolean(this.closeVisit || this.closeDate);
    }

    public get isTodayOpen(): boolean {
        if (this.isDeleted()) {
            return false;
        }

        return this.openDate >= startOfToday() && this.openDate < endOfToday();
    }

    public get isTodayClosed(): boolean {
        if (this.isDeleted() || this.closeDate === null) {
            return false;
        }

        return this.closeDate >= startOfToday() && this.closeDate < endOfToday();
    }

    public get isTodayModified(): boolean {
        if (this.isDeleted()) {
            return false;
        }

        return this.lastModified >= startOfToday() && this.lastModified < endOfToday();
    }

    public get photos(): ReadonlyArray<BugAnnotationEntity> {
        const comparator = comparatorForType("date", "asc");
        return this.annotations.slice().sort((lhs, rhs) => comparator(lhs.creationDate, rhs.creationDate));
    }

    public get coverPhoto(): BugAnnotationEntity | null {
        return this.photos.length > 0 ? this.photos[0] : null;
    }
}
